/*
 * Copyright (c) 2021, WSO2 Inc. (http://www.wso2.com). All Rights Reserved.
 *
 * This software is the property of WSO2 Inc. and its suppliers, if any.
 * Dissemination of any information or reproduction of any material contained
 * herein is strictly forbidden, unless permitted by WSO2 in accordance with
 * the WSO2 Commercial License available at http://wso2.com/licenses.
 * For specific language governing the permissions and limitations under
 * this license, please see the license as well as any agreement you’ve
 * entered into with WSO2 governing the purchase of this software and any
 * associated services.
 */

import React, { useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import {
    Box, 
    Chip, 
    CircularProgress,
    Dialog, 
    DialogActions, 
    DialogContent, 
    DialogContentText, 
    DialogTitle, 
    Typography
} from "@material-ui/core";
import { Info } from "@material-ui/icons";
import CopyTextArea from "../../../elements/CopyTextArea";
import GreyOutlinedButton from "../../../elements/GreyOutlinedButton";
import { useStyles } from './style';
import CustomAutoComplete from "../../../elements/CustomAutoComplete/CustomAutoComplete";
import { getScopeName, isInvalidValidScopeName } from "../../../util/token-generation";
import PrimaryButton from "../../../elements/PrimaryButton";
import { PreLoaderLogo } from "../../../elements/PreLoaderLogo";

interface AccessTokenProps {
    tokenOpen: boolean,
    token: string,
    onClose: () => void
    selectedPermissions?: string[]
    handleClickGenerate? : () => void;
    isTokenGenerating?: boolean;
    setPermissions?: (permissions: string[]) => void;
    existingPermissions?: string[];
    generatedScopesForToken?: string[];
}

function AccessTokenDialog(accessTokenProps: AccessTokenProps) {
    const { 
        tokenOpen, 
        token, 
        onClose, 
        selectedPermissions,
        handleClickGenerate, 
        isTokenGenerating, 
        setPermissions, 
        existingPermissions,
        generatedScopesForToken
    } = accessTokenProps;
    const classes = useStyles();
    const intl = useIntl();
    const [errorText, setErrorText] = useState('');

    return (
        <Dialog
            open={tokenOpen}
            classes={{ paper: classes.dialog }}
            onClose={onClose}
            aria-labelledby="alert-dialog-token"
            data-testid="alert-dialog-token"
        >
            <DialogTitle id="alert-dialog-token" className={classes.dialogTitle}>
                <Typography className={classes.titleText}>
                    <FormattedMessage
                        id='application.productionKeys.oauth.tokenGeneration.dialogTitle'
                        defaultMessage='Generate Test Token'
                    />
                </Typography>
            </DialogTitle>
            <DialogContent>
                <DialogContentText id="alert-dialog-token-description" className={classes.dialogContent}>
                    {token && !isTokenGenerating ? (
                        <Box>
                            {(existingPermissions?.length ?? 0) > 0 && (
                                <Box className={classes.permissionContainer}>
                                    <Typography className={classes.infoText}>
                                        <FormattedMessage
                                            id='components.shared.AccessTokenDialog.tokenGeneration.labels.scopes'
                                            defaultMessage="Request Permisssions (Scopes)"
                                        />
                                    </Typography>
                                    <CustomAutoComplete
                                        placeholder={
                                            intl.formatMessage({
                                                id: 'components.shared.AccessTokenDialog.tokenGeneration.labels.select.scopes',
                                                defaultMessage: 'Select Scopes to Add to Token',
                                            })
                                        }
                                        options={existingPermissions || []}
                                        onChange={(_, permissions) => {
                                            if (setPermissions) {
                                                setPermissions(permissions);
                                            }
                                        }}
                                        textFieldStyle={classes.textField}
                                        tagStyle={classes.tag}
                                        errorText={errorText}
                                        defaultValues={selectedPermissions}
                                        data-testid="dialog-token-generate-scope-selector"
                                    />
                                </Box>
                            )}
                            <Box className={classes.keyInfo}>
                                <Box display="flex" flexDirection="row" justifyContent="space-between" mb={1}>
                                    <Box>
                                        <Typography variant='h5' className={classes.infoHeader}>
                                            <FormattedMessage
                                                id='components.shared.AccessTokenDialog.oauth.copyToken.label'
                                                defaultMessage='Copy the access token'
                                            />
                                        </Typography>
                                    </Box>
                                    <Box display="flex">
                                        <Info className={classes.infoIcon} />
                                    </Box>
                                </Box>
                                <Box>
                                    <Typography variant='body1' className={classes.infoText}>
                                        <FormattedMessage
                                            id='components.shared.AccessTokenDialog.copyToken.instruction'
                                            defaultMessage='Please copy this generated access token as
                                            it will not be visible in the UI again.'
                                        />
                                    </Typography>
                                </Box>
                            </Box>
                            <CopyTextArea textContent={token} />
                            <Box
                                display="flex"
                                flexDirection="column"
                                className={classes.permissionList}
                                data-testid={`dialog-token-generate-permission-list`}
                            >
                                {(existingPermissions?.length ?? 0) > 0 && (generatedScopesForToken?.length ?? 0) > 0 ? (
                                    <>
                                        Response Permissions (Scopes) {generatedScopesForToken?.map((permission: string) => (
                                            <Chip
                                                className={classes.permissionChip}
                                                key={permission}
                                                size="small"
                                                label={permission}
                                                data-testid={`dialog-token-generate-scope-chip-${permission}`}
                                            />
                                        ))}
                                    </>
                                ) : (
                                    <>
                                        {selectedPermissions && selectedPermissions?.length > 0 && generatedScopesForToken === undefined &&
                                            <>
                                                Response Permissions (Scopes) {selectedPermissions.map((permission: string) => (
                                                    <Chip
                                                        className={classes.permissionChip}
                                                        key={permission}
                                                        size="small"
                                                        label={permission}
                                                        data-testid={`dialog-token-generate-scope-${getScopeName(permission)}`}
                                                    />
                                                ))}
                                            </>
                                        }
                                    </>
                                )}
                            </Box>
                        </Box>
                    ) : 
                        (<>
                            <div className={classes.loaderContainer}>
                                <PreLoaderLogo size="medium" />
                            </div>
                        </>)
                    }
                </DialogContentText>
            </DialogContent>
            {token && (
                <DialogActions className={classes.action}>
                    {(existingPermissions?.length ?? 0) > 0 && (
                        <PrimaryButton
                            onClick={handleClickGenerate}
                            className={classes.generateButton}
                            type='submit'
                            buttonLabel={intl.formatMessage({
                                id: 'components.shared.AccessTokenDialog.tokenGeneration.buttons.generateButton',
                                defaultMessage: 'Regenerate Token',
                            })}
                            startIcon={
                                isTokenGenerating &&
                                <CircularProgress size={11} color='secondary' />
                            }
                            disabled={!!errorText}
                            dataTestId='dialog-token-generate-button'
                        />
                    )}
                    <GreyOutlinedButton className={classes.greyOutlinedButton} text='Close' onClick={onClose} />
                </DialogActions>
            )}
        </Dialog>
    );
}

export default AccessTokenDialog;
